import { useState } from "react";
import {
  Alert, Box, Button, Chip, MenuItem, Paper, Stack, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, type GridColDef, type GridPaginationModel } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { otConsumptionsApi, type OtConsumptionListDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const STATUS = ["", "Draft", "Submitted", "Approved", "Posted", "Cancelled"];
const STATUS_COLOR: Record<string, "default" | "warning" | "info" | "success" | "error"> = {
  Draft: "default", Submitted: "warning", Approved: "info", Posted: "success", Cancelled: "error",
};

/** UC-FN-HOS-02 — OT consumption with implant UDI capture. */
export function OtConsumptionsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const canCreate = useAuth(s => s.hasPermission("hospital.ot.issue"));

  const query = useQuery({
    queryKey: ["ot-consumptions", status, pagination.page, pagination.pageSize],
    queryFn: () => otConsumptionsApi.list({
      status: status || undefined,
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const columns: GridColDef<OtConsumptionListDto>[] = [
    { field: "documentNo", headerName: "OT #", width: 170 },
    { field: "documentDate", headerName: "Date", width: 110,
      valueFormatter: (v) => v ? new Date(v as string).toLocaleDateString() : "" },
    { field: "uhid", headerName: "UHID", width: 130 },
    { field: "procedureCode", headerName: "Procedure", width: 130 },
    { field: "surgeon", headerName: "Surgeon", flex: 1, minWidth: 180 },
    { field: "lineCount", headerName: "Items", width: 80, type: "number" },
    { field: "implantCount", headerName: "Implants", width: 100, type: "number",
      renderCell: (p) => Number(p.value) > 0 ? (
        <Chip size="small" color="info" label={String(p.value)} />
      ) : <>{p.value as number}</> },
    { field: "status", headerName: "Status", width: 130,
      renderCell: (p) => (
        <Chip size="small" label={p.value as string}
          color={STATUS_COLOR[p.value as string] ?? "default"} />
      ) },
  ];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={600}>OT consumption</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => navigate("/hospital/ot/new")}>
            New OT consumption
          </Button>
        )}
      </Stack>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField select label="Status" value={status} size="small"
            onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
            {STATUS.map(s => <MenuItem key={s} value={s}>{s || "All"}</MenuItem>)}
          </TextField>
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []} columns={columns} loading={query.isLoading}
            paginationMode="server" rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination} onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]} disableRowSelectionOnClick
            getRowId={(row) => row.id}
          />
        </Box>
      </Paper>
    </Box>
  );
}
